import { z } from "zod";

import type { IvaUpdateAction } from "@/forms/comercial/shared/iva-special";

const RPA_MODIFICAR_CLIENTE_VARIABLES = [
  "razon_social",
  "cif",
  "nombre_comercial",
  "tipo_cliente",
  "direccion",
  "localidad",
  "codigo_postal",
  "municipio",
  "provincia",
  "pais",
  "telefono",
  "email",
  "web",
  "actividad",
  "observaciones",
  "ccc",
  "limite_riesgo",
  "cuenta_contable",
  "tipos_facturacion",
] as const;

const RPA_MODIFICAR_CLIENTE_SNAPSHOT_FIELDS = {
  razon_social: "cliente_razon_social",
  cif: "cliente_cif",
  nombre_comercial: "cliente_nombre_comercial",
  tipo_cliente: "cliente_prioridad",
  direccion: "cliente_direccion",
  localidad: "cliente_localidad",
  codigo_postal: "cliente_codigo_postal",
  municipio: "cliente_municipio",
  provincia: "cliente_provincia",
  pais: "cliente_pais",
  telefono: "cliente_telefono",
  email: "cliente_email",
  web: "cliente_web",
  actividad: "cliente_actividad",
  observaciones: "cliente_observaciones",
  ccc: "cliente_ccc",
  limite_riesgo: "cliente_limite_riesgo",
  cuenta_contable: "cliente_cuenta_contable",
  tipos_facturacion: "cliente_tipo_facturacion",
} as const satisfies Record<(typeof RPA_MODIFICAR_CLIENTE_VARIABLES)[number], string>;

const RPA_MODIFICAR_CENTRO_VARIABLES = [
  "numero_ss",
  "nombre_centro",
  "direccion_centro",
  "localidad_centro",
  "codigo_postal_centro",
  "municipio_centro",
  "provincia_centro",
  "regimen_ss",
  "cnae",
  "convenio",
  "zona_geografica",
] as const;

const RPA_MODIFICAR_CENTRO_SNAPSHOT_FIELDS = {
  numero_ss: "centro_actual_numero_ss",
  nombre_centro: "centro_actual_nombre",
  direccion_centro: "centro_actual_direccion",
  localidad_centro: "centro_actual_localidad",
  codigo_postal_centro: "centro_actual_codigo_postal",
  municipio_centro: "centro_actual_municipio",
  provincia_centro: "centro_actual_provincia",
  regimen_ss: "centro_actual_regimen_ss",
  cnae: "centro_actual_cnae",
  convenio: "centro_actual_convenio",
  zona_geografica: "centro_actual_zona_geografica",
} as const satisfies Record<(typeof RPA_MODIFICAR_CENTRO_VARIABLES)[number], string>;

const RPA_CENTER_ADDRESS_UPDATE_ACTION_VALUES = ["UNCHANGED", "SET", "CLEAR"] as const;
type RpaCenterAddressUpdateAction = (typeof RPA_CENTER_ADDRESS_UPDATE_ACTION_VALUES)[number];

const RPA_CENTER_ADDRESS_UPDATE_ACTION_FIELDS = {
  direccion_centro: "direccion_centro_update_action",
  localidad_centro: "localidad_centro_update_action",
  codigo_postal_centro: "codigo_postal_centro_update_action",
  municipio_centro: "municipio_centro_update_action",
  provincia_centro: "provincia_centro_update_action",
} as const satisfies Partial<Record<(typeof RPA_MODIFICAR_CENTRO_VARIABLES)[number], string>>;
const RPA_CENTER_ADDRESS_UPDATE_ACTION_FIELD_MAP = new Map(
  Object.entries(RPA_CENTER_ADDRESS_UPDATE_ACTION_FIELDS),
);

const RPA_MODIFICAR_CONTACTO_VARIABLES = [
  "nombre_contacto", "cargo_contacto", "tipo_id_contacto", "documento_contacto",
  "firma_contacto", "telefono_contacto", "email_contacto",
] as const;

const RPA_MODIFICAR_CONTACTO_SNAPSHOT_FIELDS = {
  nombre_contacto: "contacto_actual_nombre",
  cargo_contacto: "contacto_actual_cargo",
  tipo_id_contacto: "contacto_actual_tipo_id",
  documento_contacto: "contacto_actual_documento",
  firma_contacto: "contacto_actual_firma",
  telefono_contacto: "contacto_actual_telefono",
  email_contacto: "contacto_actual_email",
} as const satisfies Record<(typeof RPA_MODIFICAR_CONTACTO_VARIABLES)[number], string>;


type PrimitiveInput = string | number | boolean | null | undefined;
const textValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null(), z.undefined()]);
type TextValue = PrimitiveInput;
const trimText = (value: TextValue): string => String(value ?? "").trim();
const normalizeSecuritySocialNumber = (value: TextValue): string => trimText(value).replace(/[\s-]+/g, "");

export function normalizePositiveContactOrder(value: TextValue): string {
  const order = trimText(value);
  return /^[1-9]\d*$/.test(order) ? order : "";
}

export function normalizePositiveCenterOrder(value: TextValue): string {
  const order = trimText(value);
  return /^[1-9]\d*$/.test(order) ? order : "";
}

export function contactCenterIdFromReference(value: TextValue): string {
  const parts = trimText(value).split(":");
  if (parts.length !== 3) {
    return "";
  }

  const [clientId, centerId, contactId] = parts.map((part) => part.trim());
  return clientId && centerId && contactId ? centerId : "";
}

const normalizeFlag = (value: TextValue): boolean =>
  value === true || value === "true" || value === "S";

const normalizeIvaCause = (iva: boolean, value: TextValue): string =>
  iva ? trimText(value).toUpperCase() : "";

function deriveIvaUpdateAction(
  currentIva: boolean,
  currentReason: string,
  proposedIva: boolean,
  proposedReason: string,
): IvaUpdateAction {
  if (currentIva !== proposedIva) {
    return proposedIva ? "ENABLE" : "DISABLE";
  }
  const normalizedCurrentReason = normalizeIvaCause(currentIva, currentReason);
  const normalizedProposedReason = normalizeIvaCause(proposedIva, proposedReason);
  if (proposedIva && normalizedCurrentReason !== normalizedProposedReason) {
    return "CHANGE_REASON";
  }
  return "UNCHANGED";
}

const normalizeProductFamilyKey = (value: TextValue): string =>
  trimText(value)
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export function isEttProductFamily(value: TextValue): boolean {
  return normalizeProductFamilyKey(value) === "ett";
}

function familiaFromBaseDatosM4(value: TextValue): "ETT" | "NO" {
  return isEttProductFamily(value) ? "ETT" : "NO";
}

function normalizeSiNoForRpa(value: TextValue): "S" | "N" {
  if (value === true || value === "true" || value === "S") {
    return "S";
  }
  return "N";
}

const RPA_CONTACTO_CAMPOS_COMUNES = [
  "orden_contacto",
  "nombre_contacto",
  "cargo_contacto",
  "telefono_contacto",
  "email_contacto",
] as const;

const RPA_CONTACTO_CAMPOS_ETT = [
  "tipo_id_contacto",
  "documento_contacto",
  "firma_contacto",
] as const;

const RPA_CENTRO_CAMPOS_SOLO_ALTA = [
  "numero_ss",
  "regimen_ss",
  "regimen_ss_label",
  "nombre_centro",
  "direccion_centro",
  "localidad_centro",
  "codigo_postal_centro",
  "municipio_centro",
  "provincia_centro",
  "provincia_centro_label",
  "pais_centro",
  "pais_centro_label",
  "cnae",
  "cnae_label",
] as const;

function normalizeProvinceCodeForRpa(value: TextValue): string {
  const trimmed = trimText(value);
  if (!trimmed) {
    return "";
  }
  if (/^\d+$/.test(trimmed)) {
    return trimmed.padStart(2, "0");
  }
  return trimmed;
}

const normalizeCatalogLookupKey = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export function resolveProvinciaFormOption(
  value: TextValue,
  options: ReadonlyArray<{ value: string; label: string }>,
): { value: string; label: string } | null {
  const trimmed = trimText(value);
  if (!trimmed || options.length === 0) {
    return null;
  }

  const paddedCode = /^\d+$/.test(trimmed) ? trimmed.padStart(2, "0") : trimmed;
  const byValue = options.find(
    (option) => option.value === trimmed || option.value === paddedCode,
  );
  if (byValue) {
    return byValue;
  }

  const normalizedInput = normalizeCatalogLookupKey(trimmed);
  const byLabel = options.find(
    (option) => normalizeCatalogLookupKey(option.label) === normalizedInput,
  );
  return byLabel ?? null;
}

const PAIS_LOOKUP_ALIASES = new Map([
  ["spain", "ES"],
  ["espana", "ES"],
  ["espania", "ES"],
]);

export function resolvePaisFormOption(
  value: TextValue,
  options: ReadonlyArray<{ value: string; label: string }>,
): { value: string; label: string } | null {
  const trimmed = trimText(value);
  if (!trimmed || options.length === 0) {
    return null;
  }

  const byValue = options.find(
    (option) => option.value.toUpperCase() === trimmed.toUpperCase(),
  );
  if (byValue) {
    return byValue;
  }

  const normalizedInput = normalizeCatalogLookupKey(trimmed);
  const aliasCode = PAIS_LOOKUP_ALIASES.get(normalizedInput);
  if (aliasCode) {
    const byAlias = options.find((option) => option.value === aliasCode);
    if (byAlias) {
      return byAlias;
    }
  }

  const byLabel = options.find(
    (option) => normalizeCatalogLookupKey(option.label) === normalizedInput,
  );
  return byLabel ?? null;
}

function normalizePaisCodeForRpa(value: TextValue): string {
  return trimText(value).toUpperCase();
}

type GestionClientesRpaPayload = {
  gestion_cliente_accion: string;
  base_datos_m4: string;
  net4_sync_requested?: "S" | "N";
  cliente?: string;
  cliente_bloqueado?: TextValue;
  desbloquear_cliente?: TextValue;
  motivo_desbloqueo_cliente?: string;
  tiene_datos_cliente?: TextValue;
  tiene_datos_centro?: TextValue;
  tiene_datos_contacto?: TextValue;
  familia_de_productos?: string;
  familia_de_productos_label?: string;
  cif?: string;
  cliente_cif?: string;
  zona_geografica?: string;
  convenio?: string;
  tipo_cliente?: string;
  numero_ss?: string;
  regimen_ss?: string;
  nombre_centro?: string;
  direccion_centro?: string;
  localidad_centro?: string;
  codigo_postal_centro?: string;
  municipio_centro?: string;
  provincia_centro?: string;
  direccion_centro_update_action?: RpaCenterAddressUpdateAction;
  localidad_centro_update_action?: RpaCenterAddressUpdateAction;
  codigo_postal_centro_update_action?: RpaCenterAddressUpdateAction;
  municipio_centro_update_action?: RpaCenterAddressUpdateAction;
  provincia_centro_update_action?: RpaCenterAddressUpdateAction;
  pais_centro?: string;
  provincia?: string;
  cnae?: string;
  forma_pago?: string;
  forma_pago_label?: string;
  finanzas_aprobacion_condiciones_pago?: TextValue;
  revision_rpa_modificar_cliente?: TextValue;
  iva?: TextValue;
  tipo_iva?: string;
  tipo_iva_label?: string;
  cliente_iva?: TextValue;
  cliente_tipo_iva?: string;
  cliente_tipo_iva_label?: string;
  iva_update_action?: IvaUpdateAction;
  id_cliente?: string;
  crear_centro?: TextValue;
  crear_contacto?: TextValue;
  net4_contact_order_reservation_key?: string;
  net4_contact_reservation_consumed?: TextValue;
  numero_centro?: string;
  centro_modificar_id?: string;
  orden_contacto?: string;
  numero_contacto?: string;
  contacto_erp?: string;
  contacto_centro_id?: string;
  nombre_contacto?: string;
  cargo_contacto?: string;
  tipo_id_contacto?: string;
  tipo_id_contacto_label?: string;
  documento_contacto?: string;
  firma_contacto?: string;
  firma_contacto_label?: string;
  telefono_contacto?: string;
  email_contacto?: string;
};

type RpaPayloadValue = TextValue;
interface MutableRpaPayload {
  [key: string]: RpaPayloadValue;
}

const copyRpaPayload = (data: GestionClientesRpaPayload): MutableRpaPayload => {
  const payload: MutableRpaPayload = {};
  for (const [key, value] of Object.entries(data)) {
    payload[key] = textValueSchema.parse(value);
  }
  return payload;
};

const RPA_CLIENT_MIRROR_FIELDS = [
  "razon_social",
  "cif",
  "nombre_comercial",
  "tipo_cliente",
  "direccion",
  "localidad",
  "codigo_postal",
  "municipio",
  "provincia",
  "pais",
  "telefono",
  "email",
  "web",
  "actividad",
  "observaciones",
  "ccc",
  "swift",
  "limite_riesgo",
  "vendedor",
  "industria",
  "cuenta_contable",
  "tipos_facturacion",
  "tipos_facturacion_label",
  "iva",
  "tipo_iva",
  "tipo_iva_label",
  "numero_ss",
  "regimen_ss",
  "regimen_ss_label",
  "zona_geografica",
  "convenio",
  "convenio_label",
  "crear_contacto",
  "orden_contacto",
  "nombre_contacto",
  "cargo_contacto",
  "telefono_contacto",
  "email_contacto",
  "tipo_id_contacto",
  "tipo_id_contacto_label",
  "documento_contacto",
  "firma_contacto",
  "firma_contacto_label",
  "codigo_centro",
] as const;

const RPA_MODIFICAR_CLIENTE_CAMPOS_AISLADOS = [
  ...RPA_CENTRO_CAMPOS_SOLO_ALTA,
  ...RPA_CONTACTO_CAMPOS_COMUNES,
  ...RPA_CONTACTO_CAMPOS_ETT,
  "contacto_erp",
  "contacto_erp_label",
  "contacto_centro_id",
  "codigo_centro",
  "numero_centro",
  "centro_modificar_id",
  "numero_contacto",
  "tipo_id_contacto_label",
  "firma_contacto_label",
] as const;

function buildModificarClientePayload(data: GestionClientesRpaPayload): MutableRpaPayload {
  const familia = familiaFromBaseDatosM4(data.familia_de_productos || data.base_datos_m4);
  const familiaLabel = familia === "ETT" ? "ETT" : "Servicios";
  const source = copyRpaPayload(data);
  const payload = copyRpaPayload(data);
  const unlockClient = normalizeFlag(data.desbloquear_cliente);

  for (const field of [...RPA_CLIENT_MIRROR_FIELDS, ...RPA_MODIFICAR_CLIENTE_CAMPOS_AISLADOS]) {
    payload[field] = "";
  }
  let hasClientChanges = false;
  for (const field of RPA_MODIFICAR_CLIENTE_VARIABLES) {
    const proposedValue = trimText(source[field]);
    const currentValue = trimText(source[RPA_MODIFICAR_CLIENTE_SNAPSHOT_FIELDS[field]]);
    const changedValue = proposedValue !== "" && proposedValue !== currentValue ? proposedValue : "";
    payload[field] = changedValue;
    hasClientChanges ||= changedValue !== "";
  }
  const currentIva = normalizeFlag(source.cliente_iva);
  const proposedIva = normalizeFlag(source.iva);
  const currentTipoIva = normalizeIvaCause(currentIva, source.cliente_tipo_iva);
  const proposedTipoIva = normalizeIvaCause(proposedIva, source.tipo_iva);
  const ivaUpdateAction = deriveIvaUpdateAction(
    currentIva,
    currentTipoIva,
    proposedIva,
    proposedTipoIva,
  );
  payload.iva = proposedIva;
  payload.tipo_iva = proposedIva ? proposedTipoIva : "";
  payload.tipo_iva_label = proposedIva ? trimText(source.tipo_iva_label) : "";
  payload.iva_update_action = ivaUpdateAction;
  hasClientChanges ||= ivaUpdateAction !== "UNCHANGED";
  const centerOrder = normalizePositiveCenterOrder(source.numero_centro);
  const centerSelected = centerOrder !== "";
  const proposedCenterChanges = RPA_MODIFICAR_CENTRO_VARIABLES.some((field) => {
    const proposedValue = trimText(source[field]);
    const currentValue = trimText(source[RPA_MODIFICAR_CENTRO_SNAPSHOT_FIELDS[field]]);
    return proposedValue !== currentValue && (
      RPA_CENTER_ADDRESS_UPDATE_ACTION_FIELD_MAP.has(field) || proposedValue !== ""
    );
  });
  const centerId = trimText(source.centro_modificar_id);
  if (proposedCenterChanges && (!centerSelected || !/^[1-9]\d*$/.test(centerId))) {
    throw new Error("El centro seleccionado no tiene una identidad NET4 válida");
  }
  let hasCenterChanges = false;
  for (const field of RPA_MODIFICAR_CENTRO_VARIABLES) {
    const proposedValue = trimText(source[field]);
    const currentValue = trimText(source[RPA_MODIFICAR_CENTRO_SNAPSHOT_FIELDS[field]]);
    const actionField = RPA_CENTER_ADDRESS_UPDATE_ACTION_FIELD_MAP.get(field);
    if (actionField) {
      const action: RpaCenterAddressUpdateAction = !centerSelected || proposedValue === currentValue
        ? "UNCHANGED"
        : proposedValue === ""
          ? "CLEAR"
          : "SET";
      payload[actionField] = action;
      payload[field] = action === "SET" ? proposedValue : "";
      hasCenterChanges ||= action !== "UNCHANGED";
      continue;
    }
    const changedValue = centerSelected && proposedValue !== "" && proposedValue !== currentValue ? proposedValue : "";
    payload[field] = changedValue;
    hasCenterChanges ||= changedValue !== "";
  }
  const contactOrder = normalizePositiveContactOrder(source.numero_contacto);
  const contactSelected = contactOrder !== "";
  let hasContactChanges = false;
  for (const field of RPA_MODIFICAR_CONTACTO_VARIABLES) {
    const proposedValue = trimText(source[field]);
    const currentValue = trimText(source[RPA_MODIFICAR_CONTACTO_SNAPSHOT_FIELDS[field]]);
    const changedValue = contactSelected && proposedValue !== "" && proposedValue !== currentValue ? proposedValue : "";
    payload[field] = changedValue;
    hasContactChanges ||= changedValue !== "";
  }
  payload.numero_centro = hasCenterChanges || hasContactChanges ? centerOrder : "";
  payload.centro_modificar_id = hasCenterChanges ? centerId : "";
  payload.numero_contacto = hasContactChanges ? contactOrder : "";
  payload.contacto_erp = hasContactChanges ? trimText(source.contacto_erp) : "";
  payload.contacto_centro_id = hasContactChanges ? trimText(source.contacto_centro_id) : "";

  return {
    ...payload,
    id_cliente: trimText(data.id_cliente) || trimText(data.cliente),
    familia_de_productos: familia,
    familia_de_productos_label: familiaLabel,
    forma_pago: "",
    forma_pago_label: "",
    condicion_pago_pactada: "",
    condicion_pago_pactada_label: "",
    contexto_cambio_condiciones_pago: "",
    tipos_facturacion_label: trimText(source.tipos_facturacion_label),
    crear_centro: "N",
    crear_contacto: "N",
    tiene_datos_cliente: hasClientChanges,
    tiene_datos_centro: hasCenterChanges,
    tiene_datos_contacto: hasContactChanges,
    iva_update_action: ivaUpdateAction,
    desbloquear_cliente: unlockClient,
    motivo_desbloqueo_cliente: unlockClient
      ? trimText(data.motivo_desbloqueo_cliente)
      : "",
  };
}

function buildMinimalModificarPagoPayload(data: GestionClientesRpaPayload): MutableRpaPayload {
  const familia = familiaFromBaseDatosM4(data.familia_de_productos || data.base_datos_m4);
  const familiaLabel = familia === "ETT" ? "ETT" : "Servicios";
  const idCliente = trimText(data.id_cliente) || trimText(data.cliente);
  const currentIva = normalizeFlag(data.cliente_iva);
  const currentTipoIva = normalizeIvaCause(currentIva, data.cliente_tipo_iva);
  const cleared = copyRpaPayload(data);

  for (const field of RPA_CLIENT_MIRROR_FIELDS) {
    cleared[field] = "";
  }

  return {
    ...cleared,
    id_cliente: idCliente,
    familia_de_productos: familia,
    familia_de_productos_label: familiaLabel,
    forma_pago: trimText(data.forma_pago),
    forma_pago_label: trimText(data.forma_pago_label),
    tiene_datos_cliente: true,
    tiene_datos_centro: false,
    centro_modificar_id: "",
    tiene_datos_contacto: false,
    iva_update_action: "UNCHANGED",
    iva: currentIva,
    tipo_iva: currentTipoIva,
    tipo_iva_label: currentIva ? trimText(data.cliente_tipo_iva_label) : "",
    desbloquear_cliente: false,
    motivo_desbloqueo_cliente: "",
  };
}

function buildCentroContactoRpaPayload(
  data: GestionClientesRpaPayload,
  options: { crearCentro: "S" | "N" },
): MutableRpaPayload {
  const familia = familiaFromBaseDatosM4(data.familia_de_productos || data.base_datos_m4);
  const familiaLabel = familia === "ETT" ? "ETT" : "Servicios";
  const cif = trimText(data.cliente_cif);
  const zonaGeografica = normalizeProvinceCodeForRpa(
    data.zona_geografica || data.provincia_centro,
  );
  const crearContacto = options.crearCentro === "S" || normalizeFlag(data.net4_contact_reservation_consumed)
    ? "N"
    : normalizeSiNoForRpa(data.crear_contacto);
  const payload: MutableRpaPayload = {
    ...copyRpaPayload(data),
    net4_sync_requested: "S",
    id_cliente: trimText(data.id_cliente) || trimText(data.cliente),
    familia_de_productos: familia,
    familia_de_productos_label: familiaLabel,
    crear_centro: options.crearCentro,
    crear_contacto: crearContacto,
    cif,
    cliente_cif: trimText(data.cliente_cif) || cif,
    zona_geografica: zonaGeografica,
    provincia: zonaGeografica,
    convenio: trimText(data.convenio),
    tipo_cliente: trimText(data.tipo_cliente),
    numero_ss: normalizeSecuritySocialNumber(data.numero_ss),
    regimen_ss: trimText(data.regimen_ss),
    nombre_centro: trimText(data.nombre_centro),
    direccion_centro: trimText(data.direccion_centro),
    localidad_centro: trimText(data.localidad_centro),
    codigo_postal_centro: trimText(data.codigo_postal_centro),
    municipio_centro: trimText(data.municipio_centro),
    provincia_centro: normalizeProvinceCodeForRpa(data.provincia_centro),
    pais_centro: normalizePaisCodeForRpa(data.pais_centro) || "ES",
    cnae: trimText(data.cnae),
    numero_centro: normalizePositiveCenterOrder(data.numero_centro),
    orden_contacto: trimText(data.orden_contacto),
    nombre_contacto: trimText(data.nombre_contacto),
    cargo_contacto: trimText(data.cargo_contacto),
    telefono_contacto: trimText(data.telefono_contacto),
    email_contacto: trimText(data.email_contacto),
    tipo_id_contacto: trimText(data.tipo_id_contacto),
    tipo_id_contacto_label: trimText(data.tipo_id_contacto_label),
    documento_contacto: trimText(data.documento_contacto),
    firma_contacto: normalizeSiNoForRpa(data.firma_contacto),
    firma_contacto_label: trimText(data.firma_contacto_label),
  };
  delete payload.codigo_centro;

  if (options.crearCentro === "S") {
    payload.net4_contact_order_reservation_key = "";
    payload.net4_contact_reservation_consumed = false;
  }

  if (options.crearCentro === "N") {
    for (const field of RPA_CENTRO_CAMPOS_SOLO_ALTA) {
      payload[field] = "";
    }
  }

  if (crearContacto === "N") {
    for (const field of [
      ...RPA_CONTACTO_CAMPOS_COMUNES,
      ...RPA_CONTACTO_CAMPOS_ETT,
      "tipo_id_contacto_label",
      "firma_contacto_label",
    ]) {
      payload[field] = "";
    }
  } else if (!isEttProductFamily(familia)) {
    for (const field of RPA_CONTACTO_CAMPOS_ETT) {
      payload[field] = "";
    }
    payload.tipo_id_contacto_label = "";
    payload.firma_contacto_label = "";
  }

  return payload;
}

export function normalizeGestionClientesForRpa(data: GestionClientesRpaPayload): MutableRpaPayload {
  if (data.gestion_cliente_accion === "crear_centro") {
    return buildCentroContactoRpaPayload(data, { crearCentro: "S" });
  }

  if (data.gestion_cliente_accion === "consultar_contactos") {
    return buildCentroContactoRpaPayload(data, { crearCentro: "N" });
  }

  if (data.gestion_cliente_accion === "modificar_cliente") {
    return buildModificarClientePayload(data);
  }

  if (data.gestion_cliente_accion === "cambiar_condiciones_pago") {
    const approved = normalizeFlag(data.finanzas_aprobacion_condiciones_pago);
    const revisionRpa = normalizeFlag(data.revision_rpa_modificar_cliente);

    if (!approved && !revisionRpa) {
      return {
        ...data,
        iva_update_action: "UNCHANGED",
        solicitud_cambio_condiciones_enviada: true,
      };
    }

    return buildMinimalModificarPagoPayload(data);
  }

  return data;
}
